import React, { useEffect, useState } from 'react';
import { Wrench, Save, Eye, EyeOff, RefreshCw, AlertCircle, CheckCircle2, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * HptProxySettings — Settings → System → HPT proxy.
 *
 * Lets an admin paste (and later replace) the shared `X-AR-Proxy`
 * secret used by our reverse proxy to talk to `hpt.auto-remap.com`.
 * We deliberately store it in Mongo `app_config` rather than
 * `backend/.env` so:
 *
 *   • the operator can rotate the secret from the UI when the upstream
 *     owner issues a new one, without touching env vars or redeploying;
 *   • local, preview, and prod can be brought up with an empty proxy
 *     and configured on the fly (fresh clone works out of the box);
 *   • the env value still works as a fallback for new environments
 *     that haven't been provisioned yet.
 *
 * The raw secret NEVER leaves the backend — we display only a masked
 * preview (`••••••••abcd`) so the admin can confirm they pasted the
 * right value.
 */
export default function HptProxySettings({ token }) {
  const [cfg, setCfg] = useState(null);
  const [secret, setSecret] = useState('');
  const [upstream, setUpstream] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}
  const [egressIp, setEgressIp] = useState('');

  const load = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/tools/hpt-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      setCfg(j);
      setUpstream(j.upstream || '');
    } catch (e) {
      setMsg({ type: 'error', text: `Load failed: ${e}` });
    }
  };

  const loadEgress = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/tools/hpt-egress-ip`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      setEgressIp(j.egress_ip || '');
    } catch {
      setEgressIp('');
    }
  };

  useEffect(() => { load(); loadEgress(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/tools/hpt-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          secret: secret,
          upstream: upstream || 'https://hpt.auto-remap.com',
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setSecret('');
      setMsg({ type: 'success', text: 'Saved. Proxy will use the new secret immediately.' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: String(e.message || e) });
    } finally {
      setSaving(false);
    }
  };

  const clearSecret = async () => {
    if (!window.confirm('Clear the stored secret? Proxy will fall back to backend .env; if that is also empty, the Tools panel will stop working.')) return;
    setSecret('');
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/tools/hpt-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ secret: '', upstream }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMsg({ type: 'success', text: 'DB override cleared.' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: String(e.message || e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="hpt-proxy-settings">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-1">
          <Wrench className="w-5 h-5 text-slate-700" />
          <h2 className="text-lg font-semibold">HPT proxy</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Reverse-proxy credentials for the external tuning tools server
          (<code className="text-xs bg-slate-100 px-1 rounded">hpt.auto-remap.com</code>).
          Paste the <code className="text-xs bg-slate-100 px-1 rounded">X-AR-Proxy</code> secret
          issued by the tools owner. Stored in Mongo, replaces the value from
          <code className="text-xs bg-slate-100 px-1 rounded"> backend/.env</code>.
        </p>

        {/* Status */}
        {cfg && (
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatusPill
              ok={cfg.configured}
              label={cfg.configured ? 'Configured' : 'Not configured'}
              testId="hpt-status-pill"
            />
            <div className="text-xs text-slate-600 bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
              <div className="uppercase tracking-wide text-[10px] text-slate-400 mb-0.5">Source</div>
              <div className="font-mono" data-testid="hpt-source">{cfg.source}</div>
            </div>
            <div className="text-xs text-slate-600 bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
              <div className="uppercase tracking-wide text-[10px] text-slate-400 mb-0.5">Current secret</div>
              <div className="font-mono truncate" data-testid="hpt-secret-masked">{cfg.secret_masked || '—'}</div>
            </div>
          </div>
        )}

        {/* Secret input */}
        <label className="block text-xs font-medium text-slate-600 mb-1">New secret</label>
        <div className="flex gap-2 mb-3">
          <input
            type={showSecret ? 'text' : 'password'}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="Paste the X-AR-Proxy value here"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="hpt-secret-input"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => setShowSecret(!showSecret)}
            className="px-3 py-2 border border-slate-300 rounded-md hover:bg-slate-50"
            title={showSecret ? 'Hide' : 'Show'}
            data-testid="hpt-secret-toggle"
          >
            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        {/* Upstream URL */}
        <label className="block text-xs font-medium text-slate-600 mb-1">Upstream URL</label>
        <input
          type="text"
          value={upstream}
          onChange={(e) => setUpstream(e.target.value)}
          placeholder="https://hpt.auto-remap.com"
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500 mb-4"
          data-testid="hpt-upstream-input"
        />

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={save}
            disabled={saving || !secret}
            className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-md text-sm hover:bg-cyan-700 disabled:opacity-50"
            data-testid="hpt-save-btn"
          >
            <Save className="w-4 h-4" /> Save
          </button>
          <button
            onClick={load}
            disabled={saving}
            className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50"
            data-testid="hpt-reload-btn"
          >
            <RefreshCw className="w-4 h-4" /> Reload
          </button>
          {cfg && cfg.source === 'db' && (
            <button
              onClick={clearSecret}
              disabled={saving}
              className="inline-flex items-center gap-2 px-3 py-2 border border-rose-300 text-rose-700 rounded-md text-sm hover:bg-rose-50"
              data-testid="hpt-clear-btn"
            >
              Clear DB override
            </button>
          )}
        </div>

        {msg && (
          <div
            className={`mt-3 text-sm px-3 py-2 rounded-md ${
              msg.type === 'success'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-rose-50 border border-rose-200 text-rose-800'
            }`}
            data-testid="hpt-msg"
          >
            {msg.text}
          </div>
        )}
      </div>

      {/* Egress IP block — ops handoff to upstream owner */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="text-sm font-semibold mb-2">Backend egress IP</h3>
        <p className="text-xs text-slate-600 mb-3">
          Give this address to the upstream owner so they can add it to
          their allow-list. Detected via <code className="bg-slate-100 px-1 rounded">api.ipify.org</code>{' '}
          from this backend.
        </p>
        <div className="flex items-center gap-2">
          <code
            className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm font-mono"
            data-testid="hpt-egress-ip"
          >
            {egressIp || '—'}
          </code>
          <button
            onClick={() => navigator.clipboard?.writeText(egressIp)}
            disabled={!egressIp}
            className="px-3 py-2 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
            title="Copy"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={loadEgress}
            className="px-3 py-2 border border-slate-300 rounded-md hover:bg-slate-50"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ ok, label, testId }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium border ${
        ok
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
          : 'bg-rose-50 border-rose-200 text-rose-700'
      }`}
      data-testid={testId}
    >
      {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {label}
    </div>
  );
}
